import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { addItems, changeLine, fetchCart } from '@theme/holdra-cart';

/**
 * Cart drawer extras: the "buy 2, get 1 free" upgrade card, cross-sell rows,
 * and the free gift that follows the cart total.
 */

/**
 * Upgrade card: raises the first bra line to the next "free bra" quantity
 * through the surrounding cart-items-component, which re-renders the drawer.
 *
 * @typedef {object} CartUpsellRefs
 * @property {HTMLButtonElement} button
 *
 * @extends {Component<CartUpsellRefs>}
 */
class CartUpsellComponent extends Component {
  requiredRefs = ['button'];

  upgrade() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItems = this.closest('cart-items-component');
    const line = Number(this.dataset.line);
    const quantity = Number(this.dataset.quantity);

    if (!cartItems || typeof cartItems.updateQuantity !== 'function' || !line || !quantity) return;

    this.refs.button.disabled = true;
    cartItems.updateQuantity({ line, quantity, action: 'change' });
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}

/**
 * One cross-sell row: adds the chosen variant to the cart.
 *
 * @typedef {object} CartCrossSellRefs
 * @property {HTMLSelectElement | HTMLInputElement} variant
 * @property {HTMLButtonElement} button
 *
 * @extends {Component<CartCrossSellRefs>}
 */
class CartCrossSellComponent extends Component {
  requiredRefs = ['variant', 'button'];

  async add() {
    const { button, variant } = this.refs;
    if (button.getAttribute('aria-busy') === 'true' || !variant.value) return;

    button.setAttribute('aria-busy', 'true');
    // A mix-and-match pack is added with its default colours on the order line
    const { propertyName, propertyValue } = this.dataset;
    const properties = propertyName && propertyValue ? { [propertyName]: propertyValue } : undefined;
    const result = await addItems(this, [{ id: variant.value, quantity: 1, properties }], {
      context: 'cart',
      origin: 'cart-cross-sell',
    });
    // On success the drawer re-renders and this row disappears; on failure let the shopper retry.
    if (!result.ok) button.removeAttribute('aria-busy');
  }
}

if (!customElements.get('cart-cross-sell-component')) {
  customElements.define('cart-cross-sell-component', CartCrossSellComponent);
}


/**
 * Free gift: keeps exactly one unit of the gift product (a 0 kr product) in the cart while the cart total
 * after discounts, gift excluded, is at or above the threshold, and none below it. Every line of the gift
 * product counts, including one added some other way (e.g. from its product URL).
 *
 * Loop safety:
 * - The amount excludes the gift, so adding or removing the gift never changes the decision.
 * - Runs are serialised; changes made meanwhile trigger at most one follow-up run with a fresh cart.
 * - The same change is never attempted twice in one run, and at most three changes per run.
 * - A failed change (e.g. gift sold out) is not retried until the cart itself changes.
 * - If the gift somehow isn't free in the cart, it is removed and not re-added until the cart changes,
 *   so shoppers are never charged for it.
 */
const GIFT_PROPERTY = '_gift';

/**
 * @typedef {{ threshold: number, giftProductId: number, giftVariantId: number, giftAvailable: boolean,
 *   checkOnLoad: boolean }} GiftConfig
 */

/** @returns {GiftConfig | null} */
function readGiftConfig() {
  const element = document.getElementById('CartGiftConfig');
  if (!element?.textContent) return null;
  try {
    return JSON.parse(element.textContent);
  } catch {
    return null;
  }
}

/**
 * The cart's contents apart from the gift, as a comparable string.
 * @param {Array<any>} items
 * @param {GiftConfig} config
 */
function cartState(items, config) {
  return items
    .filter((item) => item.product_id !== config.giftProductId)
    .map((item) => `${item.key}:${item.quantity}`)
    .sort()
    .join('|');
}

class GiftKeeper {
  #running = false;
  #rerun = false;
  /** @type {string | null} Signature of a cart state where a change failed. */
  #failedFor = null;
  /** @type {string | null} Cart contents (gift excluded) for which the gift turned out not to be free. */
  #notFreeFor = null;

  constructor() {
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise?.then(() => this.#schedule()).catch(() => this.#schedule());
    });

    if (readGiftConfig()?.checkOnLoad) this.#schedule();
  }

  #schedule() {
    if (this.#running) {
      this.#rerun = true;
      return;
    }
    this.#run();
  }

  async #run() {
    this.#running = true;
    /** @type {Set<string>} Changes attempted in this run; the same one is never tried twice. */
    const attempted = new Set();
    try {
      do {
        this.#rerun = false;
        const config = readGiftConfig();
        if (!config) return;

        const cart = await fetchCart();
        const action = this.#decide(cart, config);
        if (!action) continue;

        const signature = `${action.type}:${action.key ?? ''}:${cart.token}:${cartState(cart.items ?? [], config)}`;
        if (signature === this.#failedFor || attempted.has(signature) || attempted.size >= 3) break;
        attempted.add(signature);

        const result =
          action.type === 'add'
            ? await addItems(
                document.body,
                [{ id: config.giftVariantId, quantity: 1, properties: { [GIFT_PROPERTY]: '1' } }],
                { action: 'update', context: 'cart', origin: 'gift' }
              )
            : await changeLine(document.body, action.key, action.quantity, { origin: 'gift' });

        if (result.ok) {
          this.#failedFor = null;
          this.#rerun = true; // verify once against the fresh cart
        } else {
          this.#failedFor = signature;
        }
      } while (this.#rerun);
    } catch (error) {
      console.warn('[gift] Could not sync the free gift:', error);
    } finally {
      this.#running = false;
    }
  }

  /**
   * @param {any} cart - /cart.js response
   * @param {GiftConfig} config
   * @returns {{ type: 'add' } | { type: 'set', key: string, quantity: number } | null}
   */
  #decide(cart, config) {
    const items = /** @type {Array<any>} */ (cart.items ?? []);
    const giftLines = items.filter((item) => item.product_id === config.giftProductId);
    const giftTotal = giftLines.reduce((sum, item) => sum + item.final_line_price, 0);
    const qualifying = cart.total_price - giftTotal;
    const [firstGift, extraGift] = giftLines;

    if (config.threshold > 0 && qualifying >= config.threshold) {
      if (extraGift) return { type: 'set', key: extraGift.key, quantity: 0 };
      if (firstGift && firstGift.quantity > 1) return { type: 'set', key: firstGift.key, quantity: 1 };

      if (firstGift && firstGift.final_line_price > 0) {
        this.#notFreeFor = cartState(items, config);
        return { type: 'set', key: firstGift.key, quantity: 0 };
      }

      if (!firstGift && config.giftAvailable && this.#notFreeFor !== cartState(items, config)) {
        return { type: 'add' };
      }
      return null;
    }

    if (firstGift) return { type: 'set', key: firstGift.key, quantity: 0 };
    return null;
  }
}

if (readGiftConfig() && !window.holdraGiftKeeper) {
  window.holdraGiftKeeper = new GiftKeeper();
}
